﻿import * as React from "react";
import { createUseStyles } from "react-jss";
import type { MainPageCustomizations } from "~base/Pages/Home/Main/Customization";
import { useIntl } from "~base/Internationalization/IntlContext";
import ContactSupportOverlay from "~jrad/Components/Composite/ContactSupportOverlay";
import { Button } from "~base/Components/Atoms/Button";
import type { ConsilioBaseTheme } from "~base/Styling/ConsilioTheme";
import type { GetMetadataResponse } from "~base/Services/API/Session/Model";

interface ContactSupportButtonProps {
    onClick: () => void;
}

interface ContactSupportOverlayWrapperProps {
    setToggleMethod: (func: () => void) => void;
}

const useContactSupportButtonStyles = createUseStyles(
    (theme: ConsilioBaseTheme) => ({
        container: {
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            marginLeft: "20px",
        },
        button: {
            fontFamily: theme.constants.defaultFont,
            fontSize: "15px",
            fontWeight: 500,
            color: theme.constants.primaryColor,

            "&:hover, &:focus": {
                color: "#000000",
            },
        },
    }),
    { name: "ContactSupportButton" }
);

export const ContactSupportButton: React.FunctionComponent<
    ContactSupportButtonProps
> = ({ onClick }) => {
    const intl = useIntl();
    const classes = useContactSupportButtonStyles();

    return (
        <div className={classes.container}>
            <Button
                testIdentifier="ContactSupport"
                variant="tertiary"
                onClick={() => onClick()}
                className={classes.button}
            >
                {intl.formatText("VisiFinance.UI.Main.ContactSupport")}
            </Button>
        </div>
    );
};

export const ContactSupportOverlayWrapper: React.FunctionComponent<
    ContactSupportOverlayWrapperProps
> = ({ setToggleMethod }) => {
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        setToggleMethod(() => {
            setOpen(!open);
        });
    }, [setToggleMethod, open]);

    return <ContactSupportOverlay show={open} onClose={() => setOpen(false)} />;
};

const invoker = (func: () => void): void => {
    invokeMethod = func;
};

let invokeMethod: () => void = null;

const toggleDialog = () => {
    if (invokeMethod) {
        invokeMethod();
    }
};

export default {
    enableSearch: (sessionMetadata: GetMetadataResponse) => true,
    searchLocaleOverride: (locale) => (locale == "en-NL" ? "en-US" : locale),
    useTextCodeApplicationTitle: true,
    // ContactSupportOverlay is listening to stepSpec, but the searchFooterComponent is only mounted
    // when the search overlay is open. So we had to separate them and let the overlay reside
    // in the main component where it can properly subscribe to the change.
    // guide service is not available here since it's outside the guide container.
    searchFooterComponents: (
        <ContactSupportButton onClick={() => toggleDialog()} />
    ),
    pageAdditionalComponents: (
        <ContactSupportOverlayWrapper setToggleMethod={invoker} />
    ),
} as MainPageCustomizations;
